import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

export type BoardView = 'day' | 'week';

/**
 * Day or week (M1-10).
 *
 * A radio group rather than two buttons or a `<select>`: this is one choice
 * between mutually exclusive options, and the radio pattern is the one that
 * tells a screen reader "1 of 2" and lets arrow keys move between them. Real
 * `<input type="radio">` elements inside a `<fieldset>`, styled to look like a
 * segmented control, so all of that comes from the platform rather than from
 * `role`/`aria-checked`/`onKeyDown` this file would otherwise have to get
 * right.
 *
 * The choice does not live here — it lives in the URL, alongside the date and
 * the technician filter, which is what makes the AC's "toggle preserves the
 * selected technician filter and date" true by construction rather than by
 * remembering to copy state across.
 */
export function ViewToggle({
  view,
  onChange,
}: {
  view: BoardView;
  onChange: (next: BoardView) => void;
}) {
  const { t } = useTranslation();
  const views: BoardView[] = ['day', 'week'];

  return (
    <fieldset className="flex items-center gap-2">
      <legend className="sr-only">{t('dispatch.viewLegend')}</legend>
      <div className="inline-flex rounded-md border border-[var(--color-input)] p-0.5">
        {views.map((option) => (
          <label
            key={option}
            className={cn(
              'cursor-pointer rounded px-3 py-1 text-sm transition-colors',
              'focus-within:ring-2 focus-within:ring-[var(--color-ring)]',
              view === option
                ? 'bg-[var(--color-primary)] font-medium text-[var(--color-primary-foreground)]'
                : 'hover:bg-[var(--color-muted)]',
            )}
          >
            <input
              type="radio"
              name="dispatch-view"
              value={option}
              checked={view === option}
              onChange={() => onChange(option)}
              // Visually replaced by the label, but still the real control —
              // `sr-only` rather than `hidden`, so it keeps its tab stop and
              // its arrow-key behaviour.
              className="sr-only"
            />
            {t(`dispatch.view.${option}`)}
          </label>
        ))}
      </div>
    </fieldset>
  );
}
